import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { DEFAULT_BLIND_STRUCTURE } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'bullets_token';
const SOUND_KEY = 'bullets_sound';

const AppContext = createContext(null);

// ---------- helpers ----------
function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function wsUrlFor(code) {
  const base = (BACKEND_URL || '').replace(/^http/, 'ws');
  return `${base}/api/ws/${code}`;
}

// ---------- Server-authoritative action model ----------
// Все действия управления часами идут через один и тот же путь: клиент делает
// оптимистический предикт эффекта на локальном стейте (через ФУНКЦИОНАЛЬНЫЙ
// setState — так серии быстрых кликов корректно накапливаются), потом
// coalesce на 30мс, потом POST /action. Сервер применяет действие атомарно,
// инкрементит `version`, рассылает broadcast. Клиенты (админ + TV) заменяют
// локальный стейт только на серверные сообщения с версией строго больше
// последней применённой — устаревшие ответы и out-of-order WS никогда не
// откатят UI назад.

const COALESCE_MS = 30;

function computeOptimistic(t, action, args, serverNow) {
  if (!t) return t;
  const blinds = t.blinds || [];
  const now = serverNow;
  const active = !!(t.isRunning && !t.isPaused);
  const durationOf = (i) => (blinds[i]?.duration || 0) * 60;
  switch (action) {
    case 'start': {
      const secs = t.timeRemainingSec != null ? t.timeRemainingSec : durationOf(t.currentLevelIndex || 0);
      return { ...t, isRunning: true, isPaused: false, endsAt: now + secs * 1000, timeRemainingSec: secs };
    }
    case 'pause': {
      const rem = t.endsAt ? Math.max(0, Math.round((t.endsAt - now) / 1000)) : (t.timeRemainingSec || 0);
      return { ...t, isPaused: true, timeRemainingSec: rem, endsAt: null };
    }
    case 'next_level':
    case 'prev_level':
    case 'goto_level':
    case 'step_level': {
      let idx;
      if (action === 'next_level') idx = Math.min((t.currentLevelIndex || 0) + 1, Math.max(0, blinds.length - 1));
      else if (action === 'prev_level') idx = Math.max((t.currentLevelIndex || 0) - 1, 0);
      else if (action === 'step_level') {
        const d = args.delta || 0;
        idx = Math.max(0, Math.min((t.currentLevelIndex || 0) + d, Math.max(0, blinds.length - 1)));
      } else {
        idx = Math.max(0, Math.min(args.index || 0, blinds.length - 1));
      }
      const dur = durationOf(idx);
      return { ...t, currentLevelIndex: idx, timeRemainingSec: dur, endsAt: active ? now + dur * 1000 : null };
    }
    case 'reset_level': {
      const dur = durationOf(t.currentLevelIndex || 0);
      return { ...t, isPaused: true, timeRemainingSec: dur, endsAt: null };
    }
    case 'adjust_time': {
      const delta = args.delta || 0;
      if (active) {
        const base = t.endsAt || now + (t.timeRemainingSec || 0) * 1000;
        return { ...t, endsAt: Math.max(now, base + delta * 1000) };
      }
      return { ...t, timeRemainingSec: Math.max(0, (t.timeRemainingSec || 0) + delta) };
    }
    case 'adjust_players_total':
    case 'set_players_total':
    case 'adjust_players_remaining':
    case 'set_players_remaining': {
      const players = { ...(t.players || {}) };
      const curTotal = players.total || 0;
      const curRem = players.remaining || 0;
      if (action === 'adjust_players_total') {
        const total = Math.max(0, curTotal + (args.delta || 0));
        players.total = total;
        players.remaining = Math.min(curRem, total);
      } else if (action === 'set_players_total') {
        const total = Math.max(0, args.value || 0);
        players.total = total;
        players.remaining = Math.min(curRem, total);
      } else if (action === 'adjust_players_remaining') {
        players.remaining = Math.max(0, Math.min(curTotal, curRem + (args.delta || 0)));
      } else {
        players.remaining = Math.max(0, Math.min(curTotal, args.value || 0));
      }
      return { ...t, players };
    }
    case 'set_banner': {
      // Оптимистичное обновление: трогаем только переданные поля, чтобы
      // сохранение текста/звука не затирало свежий enabled от тумблера.
      const cur = t.banner || { enabled: false, text: '' };
      const next = { ...t };
      if (args.enabled !== undefined || args.text !== undefined) {
        next.banner = {
          enabled: args.enabled !== undefined ? !!args.enabled : !!cur.enabled,
          text: args.text !== undefined ? String(args.text) : (cur.text || ''),
        };
      }
      if (args.clearSound) {
        next.bannerSound = null;
      } else if (args.soundDataUrl !== undefined) {
        next.bannerSound = {
          name: (args.soundName || 'banner.mp3'),
          dataUrl: args.soundDataUrl,
        };
      }
      return next;
    }
    default:
      return t;
  }
}

// Слияние аргументов последовательных coalesced action одного типа.
// Дельты (adjust_*) суммируются; set_* — последний выигрывает.
function mergeArgs(prev, next, type) {
  if (!prev) return next;
  if (type === 'adjust_time'
      || type === 'adjust_players_total'
      || type === 'adjust_players_remaining'
      || type === 'step_level') {
    return { delta: (prev.delta || 0) + (next.delta || 0) };
  }
  return { ...prev, ...next };
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [publicTournaments, setPublicTournaments] = useState({}); // { code: tournament }
  const [soundEnabled, setSoundEnabledState] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== '0'; } catch (e) { return true; }
  });
  const [loading, setLoading] = useState(true);
  // Смещение серверного времени: server_now = Date.now() + clockOffset.
  const clockOffsetMsRef = useRef(0);
  // Флаг: приняли ли первый сэмпл смещения. До этого — принимаем как есть.
  const clockOffsetInitializedRef = useRef(false);
  // Обновление offset с EMA-сглаживанием. Каждый сэмпл (WS-broadcast, GET,
  // refresh) страдает от сетевого джиттера ±100…300мс, из-за чего
  // Math.round((endsAt-now)/1000) прыгал на ±1 секунду в первые моменты
  // после действия админа. EMA гасит эти прыжки, но крупные скачки
  // (>2сек, например пересинхронизация или reload сервера) — принимаем сразу.
  const updateClockOffset = (serverNowMs) => {
    if (typeof serverNowMs !== 'number') return;
    const candidate = serverNowMs - Date.now();
    if (!clockOffsetInitializedRef.current) {
      clockOffsetMsRef.current = candidate;
      clockOffsetInitializedRef.current = true;
      return;
    }
    const prev = clockOffsetMsRef.current;
    if (Math.abs(candidate - prev) > 2000) {
      // большой скачок — резкая пересинхронизация
      clockOffsetMsRef.current = candidate;
      return;
    }
    // EMA: 85% старого + 15% нового. Сглаживает jitter, не давая таймеру
    // «плясать» после каждого admin-действия.
    clockOffsetMsRef.current = Math.round(prev * 0.85 + candidate * 0.15);
  };

  // ---------- Sync bookkeeping ----------
  // Последняя применённая версия per tournament id. Любое серверное сообщение
  // с version <= lastApplied — устаревшее, отбрасываем.
  const lastAppliedVersionRef = useRef({}); // { [id]: number }
  // Буфер серверных состояний, которые пришли, пока есть незавершённые
  // локальные действия (in-flight или в очереди на отправку). Как только всё
  // разгребли — применяем самое свежее.
  const latestServerStateRef = useRef({}); // { [id]: state }
  // Счётчик in-flight HTTP-запросов /action per tournament id.
  const inflightActionsRef = useRef({}); // { [id]: number }
  // Отложенные (coalesced) action, ждущие отправки. Ключ = id + ':' + type.
  const actionBufferRef = useRef({}); // { [key]: {timer, args} }
  // Незавершённые PATCH /banner — пока >0, чужие стейты не затирают banner.
  const bannerInflightRef = useRef({}); // { [id]: number }
  // Последний optimistic banner (синхронно) — setState в ref ещё не попал.
  const bannerOptimisticRef = useRef({}); // { [id]: { banner?, bannerSound? } }
  // Живые «зеркала» state в refs — читаются из ingestServerState для
  // сохранения тяжёлых полей (backgroundImage, sounds.dataUrl) при merge
  // lite-broadcast'ов. React setState асинхронен, поэтому нужен именно ref.
  const tournamentsRef = useRef([]);
  const publicTournamentsRef = useRef({});

  const inflightCount = (id) => inflightActionsRef.current[id] || 0;
  const bufferedCount = (id) => Object.keys(actionBufferRef.current).filter((k) => k.startsWith(id + ':')).length;
  const isTournamentBusy = (id) => inflightCount(id) > 0 || bufferedCount(id) > 0;

  useEffect(() => { tournamentsRef.current = tournaments; }, [tournaments]);
  useEffect(() => { publicTournamentsRef.current = publicTournaments; }, [publicTournaments]);

  // Хелпер: слияние lite-состояния с существующим кэшем, СОХРАНЯЯ тяжёлые
  // blob'ы (backgroundImage, sounds.dataUrl). Используется везде, где lite
  // state поступает: fetchByCode, tryApplyLatestServer, refresh.
  const mergePreservingAssets = (prev, next) => {
    if (!prev) return next;
    const merged = { ...next };
    // backgroundImage: сохраняем, если в next нет непустого значения
    if (!next.backgroundImage) {
      if (prev.backgroundImage) merged.backgroundImage = prev.backgroundImage;
    }
    // sounds: если в next нет dataUrl'ов, подтягиваем из prev по имени/типу
    if (Array.isArray(next.sounds) && Array.isArray(prev.sounds)) {
      const nextHasAudio = next.sounds.some((s) => s && s.dataUrl);
      if (!nextHasAudio) {
        merged.sounds = next.sounds.map((s) => {
          if (!s) return s;
          const match = prev.sounds.find((p) => p && p.name === s.name && p.type === s.type);
          return match && match.dataUrl ? { ...s, dataUrl: match.dataUrl } : s;
        });
      }
    }
    // bannerSound: если next пришёл без dataUrl (lite broadcast) но prev
    // имеет — сохраняем dataUrl. Если сервер прислал полное значение с
    // dataUrl — берём его. Если сервер явно очистил (null) — тоже принимаем.
    if (next.bannerSound !== undefined) {
      if (next.bannerSound && !next.bannerSound.dataUrl && prev.bannerSound && prev.bannerSound.dataUrl && prev.bannerSound.name === next.bannerSound.name) {
        merged.bannerSound = { ...next.bannerSound, dataUrl: prev.bannerSound.dataUrl };
      }
    } else if (prev.bannerSound) {
      merged.bannerSound = prev.bannerSound;
    }
    // banner: если в next ключ отсутствует (битый/неполный снимок) — не
    // затираем уже показанный баннер. Явный null/объект с сервера принимаем.
    if (next.banner === undefined && prev.banner) {
      merged.banner = prev.banner;
    }
    return merged;
  };

  // Монотонная защита от «прыжка таймера назад». Оптимистичное предсказание
  // ставит endsAt = local_click + duration*1000. Сервер получает запрос
  // спустя latency_out мс и ставит endsAt = server_now + duration*1000, что
  // на 100–500мс ПОЗЖЕ оптимистичного. Когда ответ доходит (через ~2x
  // latency), Math.round((endsAt-now)/1000) может из-за этого прыгнуть с
  // 19:59 обратно на 20:00. Здесь мы отбрасываем небольшие сдвиги endsAt
  // вперёд (< 900мс) при СОВПАДЕНИИ уровня — оставляем оптимистичный
  // endsAt. Крупные изменения (adjust_time ±60с, ±600с, смена уровня,
  // pause/start) пропускаются как есть.
  const mergeMonotonicTime = (prev, next) => {
    if (!prev || !next) return next;
    if (prev.currentLevelIndex !== next.currentLevelIndex) return next;
    if (!prev.endsAt || !next.endsAt) return next;
    const diff = next.endsAt - prev.endsAt;
    if (diff > 0 && diff < 900) {
      return { ...next, endsAt: prev.endsAt };
    }
    return next;
  };

  // Применяем buffered latestServer, если очередь этого турнира пуста.
  const tryApplyLatestServer = useCallback((id) => {
    if (isTournamentBusy(id)) return;
    const state = latestServerStateRef.current[id];
    if (!state) return;
    delete latestServerStateRef.current[id];
    lastAppliedVersionRef.current[id] = state.version || 0;
    setTournaments((ts) => ts.map((t) => {
      if (t.id !== id) return t;
      const guarded = mergeMonotonicTime(t, state);
      return mergePreservingAssets(t, guarded);
    }));
    setPublicTournaments((pt) => {
      const key = Object.keys(pt).find((k) => pt[k]?.id === id);
      if (!key) return pt;
      const guarded = mergeMonotonicTime(pt[key], state);
      return { ...pt, [key]: mergePreservingAssets(pt[key], guarded) };
    });
  }, []);

  // Приём серверного состояния (из HTTP ответа или WS broadcast).
  // Если пришло lite-состояние (без base64 backgroundImage/sounds.dataUrl —
  // такие идут в broadcast'ах после действий, чтобы не гонять мегабайты по WS
  // на каждый клик), сливаем его с уже закешированным тяжёлым состоянием
  // клиента. Без этого фон/звуки исчезали бы после первого же действия.
  const ingestServerState = useCallback((state) => {
    if (!state || !state.id) return;
    const id = state.id;
    if (typeof state.serverNowMs === 'number') {
      updateClockOffset(state.serverNowMs);
    }
    const version = state.version || 0;
    const lastApplied = lastAppliedVersionRef.current[id] || 0;
    if (version < lastApplied) return; // строго старее — отбрасываем
    // Пока турнир busy, ответы/WS копятся в latestServerStateRef.
    // HTTP при параллельных действиях может прийти out-of-order: более
    // старый снимок (без свежего banner.enabled) не должен затирать более
    // новый уже в буфере — иначе тумблер и TV мигают «вкл→выкл».
    const buffered = latestServerStateRef.current[id];
    if (buffered && (buffered.version || 0) > version) return;

    let merged = state;
    if (state._lite) {
      // Ищем текущее состояние по id: сперва в личных турнирах админа,
      // потом в публичном кэше TV.
      const prevAdmin = tournamentsRef.current.find((t) => t.id === id);
      const prevPub = Object.values(publicTournamentsRef.current || {}).find((t) => t?.id === id);
      const prev = prevAdmin || prevPub || null;
      if (prev) {
        // backgroundImage — сохраняем прежний, если broadcast его не прислал
        const bg = state.backgroundImage != null ? state.backgroundImage : prev.backgroundImage;
        // sounds — merge по name/id: если в lite приходит массив без dataUrl,
        // подставляем dataUrl из prev по совпадению имени.
        let sounds = state.sounds;
        if (Array.isArray(sounds) && Array.isArray(prev.sounds)) {
          sounds = sounds.map((s) => {
            if (s && s.dataUrl != null) return s;
            const match = prev.sounds.find((p) => p && p.name === s?.name && p.type === s?.type);
            return match ? { ...s, dataUrl: match.dataUrl } : s;
          });
        }
        merged = { ...state, backgroundImage: bg, ...(sounds ? { sounds } : {}) };
        // bannerSound: lite-broadcast приходит без dataUrl. Если prev имел
        // dataUrl под тем же именем — сохраняем его, иначе оставляем как есть.
        if (state.bannerSound && !state.bannerSound.dataUrl && prev.bannerSound && prev.bannerSound.dataUrl && prev.bannerSound.name === state.bannerSound.name) {
          merged.bannerSound = { ...state.bannerSound, dataUrl: prev.bannerSound.dataUrl };
        } else if (state.bannerSound === undefined && prev.bannerSound) {
          merged.bannerSound = prev.bannerSound;
        }
        // banner: не теряем при неполном lite-снимке (ключ отсутствует).
        if (state.banner === undefined && prev.banner) {
          merged.banner = prev.banner;
        }
      }
    }
    // Пока свой PATCH /banner в полёте — чужой pause/players WS не должен
    // откатывать optimistic banner (иначе мигание на паузе).
    if ((bannerInflightRef.current[id] || 0) > 0) {
      const opt = bannerOptimisticRef.current[id];
      if (opt) {
        if (opt.banner !== undefined) merged = { ...merged, banner: opt.banner };
        if ('bannerSound' in opt) merged = { ...merged, bannerSound: opt.bannerSound };
      } else {
        const prevAdmin = tournamentsRef.current.find((t) => t.id === id);
        const prevPub = Object.values(publicTournamentsRef.current || {}).find((t) => t?.id === id);
        const prev = prevAdmin || prevPub || null;
        if (prev) {
          if (prev.banner !== undefined) merged = { ...merged, banner: prev.banner };
          if (prev.bannerSound !== undefined) merged = { ...merged, bannerSound: prev.bannerSound };
        }
      }
    }
    latestServerStateRef.current[id] = merged;
    tryApplyLatestServer(id);
  }, [tryApplyLatestServer]);

  // Set/clear auth header defaults
  useEffect(() => {
    axios.defaults.headers.common = { ...(axios.defaults.headers.common || {}), ...authHeaders() };
  }, [user]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setTournaments([]);
  }, []);

  const logoutRef = useRef(logout);
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  // Мгновенный kick: любой 401 (кроме login) → logout + редирект из админки.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const url = String(error?.config?.url || '');
        if (status === 401 && !url.includes('/auth/login')) {
          logoutRef.current();
          try {
            const path = window.location.pathname || '';
            if (path.startsWith('/admin') && path !== '/admin/login') {
              window.location.assign('/admin/login');
            }
          } catch (e) { /* noop */ }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Load current user from token
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }
      try {
        const r = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        if (!cancelled) setUser(r.data.user);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Пока в админке — периодически проверяем сессию (деактивация/удаление).
  useEffect(() => {
    if (!user) return undefined;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        if (r.data?.user) setUser(r.data.user);
      } catch (e) { /* 401 handled by interceptor */ }
    };
    const timer = setInterval(tick, 15000);
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') tick();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', onVisible);
    }
    return () => {
      clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onVisible);
      }
    };
  }, [user]);

  const refreshTournaments = useCallback(async () => {
    if (!user) { setTournaments([]); return; }
    try {
      const r = await axios.get(`${API}/tournaments`, { headers: authHeaders() });
      const items = r.data || [];
      // Инициализируем lastAppliedVersion для загруженных турниров.
      items.forEach((t) => {
        if (t?.id) lastAppliedVersionRef.current[t.id] = t.version || 0;
      });
      setTournaments(items);
    } catch (e) { /* noop */ }
  }, [user]);

  useEffect(() => { refreshTournaments(); }, [refreshTournaments]);

  // ---------- Auth ----------
  const login = async (email, password) => {
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setUser(r.data.user);
      return { ok: true };
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = 'Ошибка сети';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x?.msg || String(x)).join('; ');
      else if (d && typeof d === 'object') msg = d.msg || JSON.stringify(d);
      return { ok: false, error: msg };
    }
  };

  // ---------- Admin users (super only) ----------
  const listUsers = useCallback(async () => {
    const r = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
    return r.data || [];
  }, []);

  const createUser = useCallback(async ({ email, password, name }) => {
    try {
      const r = await axios.post(`${API}/admin/users`, { email, password, name }, { headers: authHeaders() });
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map((x) => x?.msg || String(x)).join('; ') : (e.message || 'Ошибка'));
      throw new Error(msg);
    }
  }, []);

  const setUserActive = useCallback(async (id, isActive) => {
    try {
      const r = await axios.patch(`${API}/admin/users/${id}`, { isActive }, { headers: authHeaders() });
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map((x) => x?.msg || String(x)).join('; ') : (e.message || 'Ошибка'));
      throw new Error(msg);
    }
  }, []);

  const deleteUser = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers: authHeaders() });
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map((x) => x?.msg || String(x)).join('; ') : (e.message || 'Ошибка'));
      throw new Error(msg);
    }
  }, []);

  // ---------- Tournaments CRUD ----------
  const createTournament = async (payload) => {
    const body = {
      name: payload.name || 'Новый турнир',
      subtitle: payload.subtitle || '',
      descriptionLines: payload.descriptionLines || [],
      sideInfo: payload.sideInfo || '',
      startingStack: payload.startingStack || 10000,
      buyIn: payload.buyIn || 0,
      blinds: payload.blinds && payload.blinds.length ? payload.blinds : DEFAULT_BLIND_STRUCTURE,
      backgroundImage: payload.backgroundImage || '',
      showAvgStack: payload.showAvgStack !== false,
      gongOnLevel: payload.gongOnLevel !== false,
      sounds: payload.sounds || [],
      ...(payload.code ? { code: payload.code } : {})
    };
    try {
      const r = await axios.post(`${API}/tournaments`, body, { headers: authHeaders() });
      setTournaments((ts) => [r.data, ...ts]);
      if (r.data?.id) lastAppliedVersionRef.current[r.data.id] = r.data.version || 0;
      return r.data;
    } catch (e) {
      throw new Error(e?.response?.data?.detail || e.message);
    }
  };

  // PATCH — только для редактора (name, blinds, sideInfo, sounds, background, code).
  // Управление часами через PATCH больше НЕ идёт — используйте dispatchAction.
  const updateTournament = async (id, patch) => {
    // Оптимистический update
    setTournaments((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    setPublicTournaments((pt) => {
      const key = Object.keys(pt).find((k) => pt[k]?.id === id);
      if (!key) return pt;
      return { ...pt, [key]: { ...pt[key], ...patch } };
    });
    try {
      const r = await axios.patch(`${API}/tournaments/${id}`, patch, { headers: authHeaders() });
      ingestServerState(r.data);
      return r.data;
    } catch (e) {
      // Откатываем — читаем свежее с сервера
      refreshTournaments();
      throw e;
    }
  };

  const deleteTournament = async (id) => {
    setTournaments((ts) => ts.filter((t) => t.id !== id));
    try { await axios.delete(`${API}/tournaments/${id}`, { headers: authHeaders() }); }
    catch (e) { refreshTournaments(); }
  };

  const duplicateTournament = async (id) => {
    try {
      const r = await axios.post(`${API}/tournaments/${id}/duplicate`, {}, { headers: authHeaders() });
      setTournaments((ts) => [r.data, ...ts]);
      if (r.data?.id) lastAppliedVersionRef.current[r.data.id] = r.data.version || 0;
      return r.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      throw new Error(detail);
    }
  };

  // ---------- dispatchAction (главный метод управления часами) ----------
  const dispatchAction = useCallback((id, type, args = {}) => {
    // 1) Оптимистический предикт эффекта — через ФУНКЦИОНАЛЬНЫЙ setState.
    // Это критично: если пользователь быстро жмёт "След. уровень" 5 раз
    // синхронно, каждое обновление читает СВЕЖИЙ t из аргумента, поэтому
    // индекс корректно инкрементится 1→2→3→4→5, а не остаётся 1.
    const serverNow = () => Date.now() + clockOffsetMsRef.current;
    setTournaments((ts) => ts.map((t) => (t.id === id ? computeOptimistic(t, type, args, serverNow()) : t)));
    setPublicTournaments((pt) => {
      const key = Object.keys(pt).find((k) => pt[k]?.id === id);
      if (!key) return pt;
      return { ...pt, [key]: computeOptimistic(pt[key], type, args, serverNow()) };
    });

    // 2) Coalesce по (id, type). Слияние дельт для accum-действий.
    const bufKey = `${id}:${type}`;
    const existing = actionBufferRef.current[bufKey];
    const nextArgs = mergeArgs(existing?.args, args, type);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(async () => {
      delete actionBufferRef.current[bufKey];
      inflightActionsRef.current[id] = (inflightActionsRef.current[id] || 0) + 1;
      try {
        const r = await axios.post(`${API}/tournaments/${id}/action`, { type, ...nextArgs }, { headers: authHeaders() });
        ingestServerState(r.data);
      } catch (e) {
        // При ошибке — берём свежее состояние с сервера, чтобы не расходиться.
        try {
          const r = await axios.get(`${API}/tournaments/${id}`, { headers: authHeaders() });
          ingestServerState(r.data);
        } catch (_) { /* noop */ }
      } finally {
        inflightActionsRef.current[id] = Math.max(0, (inflightActionsRef.current[id] || 0) - 1);
        tryApplyLatestServer(id);
      }
    }, COALESCE_MS);

    actionBufferRef.current[bufKey] = { timer, args: nextArgs };
  }, [ingestServerState, tryApplyLatestServer]);

  // Баннер — отдельный путь вне dispatchAction/coalesce/busy.
  // Toggle на паузе больше не гоняется с pause/players в одной очереди.
  const updateBanner = useCallback(async (id, patch = {}) => {
    const applyLocal = (t) => {
      if (!t || t.id !== id) return t;
      const next = { ...t };
      if (patch.enabled !== undefined || patch.text !== undefined) {
        const cur = t.banner || { enabled: false, text: '' };
        next.banner = {
          enabled: patch.enabled !== undefined ? !!patch.enabled : !!cur.enabled,
          text: patch.text !== undefined ? String(patch.text) : (cur.text || ''),
        };
      }
      if (patch.clearSound) {
        next.bannerSound = null;
      } else if (patch.soundDataUrl !== undefined) {
        next.bannerSound = {
          name: patch.soundName || 'banner.mp3',
          dataUrl: patch.soundDataUrl,
        };
      }
      return next;
    };
    // Синхронный snapshot для protect в ingest (до flush setState → ref).
    const curAdmin = tournamentsRef.current.find((t) => t.id === id);
    const curPub = Object.values(publicTournamentsRef.current || {}).find((t) => t?.id === id);
    const base = curAdmin || curPub || { id, banner: { enabled: false, text: '' } };
    const snapped = applyLocal(base);
    bannerOptimisticRef.current[id] = {
      banner: snapped.banner,
      bannerSound: snapped.bannerSound,
    };

    setTournaments((ts) => ts.map(applyLocal));
    setPublicTournaments((pt) => {
      const key = Object.keys(pt).find((k) => pt[k]?.id === id);
      if (!key) return pt;
      return { ...pt, [key]: applyLocal(pt[key]) };
    });

    bannerInflightRef.current[id] = (bannerInflightRef.current[id] || 0) + 1;
    try {
      const r = await axios.patch(`${API}/tournaments/${id}/banner`, patch, { headers: authHeaders() });
      const prevResp = bannerOptimisticRef.current[`_resp:${id}`];
      if (!prevResp || (r.data?.version || 0) >= (prevResp.version || 0)) {
        bannerOptimisticRef.current[`_resp:${id}`] = r.data;
      }
      bannerInflightRef.current[id] = Math.max(0, (bannerInflightRef.current[id] || 0) - 1);
      if ((bannerInflightRef.current[id] || 0) === 0) {
        const latest = bannerOptimisticRef.current[`_resp:${id}`] || r.data;
        delete bannerOptimisticRef.current[id];
        delete bannerOptimisticRef.current[`_resp:${id}`];
        ingestServerState(latest);
      }
      return r.data;
    } catch (e) {
      bannerInflightRef.current[id] = Math.max(0, (bannerInflightRef.current[id] || 0) - 1);
      if ((bannerInflightRef.current[id] || 0) === 0) {
        delete bannerOptimisticRef.current[id];
        delete bannerOptimisticRef.current[`_resp:${id}`];
        try {
          const r = await axios.get(`${API}/tournaments/${id}`, { headers: authHeaders() });
          ingestServerState(r.data);
        } catch (_) { /* noop */ }
      }
      throw e;
    }
  }, [ingestServerState]);

  const getById = (id) => tournaments.find((t) => t.id === id);

  // Догрузить ПОЛНОЕ состояние турнира (с backgroundImage и sounds.dataUrl).
  // Список /api/tournaments отдаёт lite-версию для быстрого запуска админки —
  // редактор вызывает эту функцию при монтировании, чтобы получить blob'ы.
  const fetchFullTournament = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/tournaments/${id}`, { headers: authHeaders() });
      const full = r.data;
      if (full?.id) {
        // Через ingest — не затираем более новый optimistic/WS state
        // (раньше прямой setTournaments мог откатить свежий banner.enabled).
        ingestServerState(full);
      }
      return full;
    } catch (e) { return null; }
  }, [ingestServerState]);

  const fetchByCode = useCallback(async (code) => {
    try {
      const r = await axios.get(`${API}/clock/${code}`);
      const t = r.data;
      if (t?.id) {
        // Инициализируем базовую версию, если первый раз видим этот турнир.
        const last = lastAppliedVersionRef.current[t.id] || 0;
        if ((t.version || 0) >= last) {
          lastAppliedVersionRef.current[t.id] = t.version || 0;
          // MERGE, а не replace — не теряем backgroundImage/sounds, которые
          // могли быть подгружены раньше через fetchClockAssets.
          setPublicTournaments((pt) => {
            const guarded = mergeMonotonicTime(pt[code], t);
            return { ...pt, [code]: mergePreservingAssets(pt[code], guarded) };
          });
        }
      }
      if (typeof t?.serverNowMs === 'number') {
        updateClockOffset(t.serverNowMs);
      }
      return t;
    } catch (e) {
      setPublicTournaments((pt) => ({ ...pt, [code]: null }));
      return null;
    }
  }, []);

  // Асинхронная догрузка тяжёлых blob'ов (backgroundImage, sounds.dataUrl).
  // Вызывается отдельно от fetchByCode, чтобы первичный рендер TV был
  // мгновенным даже на медленных каналах (RU без VPN, 3G).
  const fetchClockAssets = useCallback(async (code) => {
    try {
      const r = await axios.get(`${API}/clock/${code}/assets`);
      const assets = r.data || {};
      setPublicTournaments((pt) => {
        const cur = pt[code];
        if (!cur) return pt;
        const merged = { ...cur };
        if (assets.backgroundImage != null && assets.backgroundImage !== '') merged.backgroundImage = assets.backgroundImage;
        if (Array.isArray(assets.sounds) && assets.sounds.length) merged.sounds = assets.sounds;
        // bannerSound: догружаем dataUrl для проигрывания на TV
        if (assets.bannerSound && assets.bannerSound.dataUrl) {
          merged.bannerSound = assets.bannerSound;
        }
        return { ...pt, [code]: merged };
      });
      return assets;
    } catch (e) { return null; }
  }, []);

  // Отправить всем подключённым TV команду «перезагрузиться» — если ТВ
  // отстал или подвис. Сервер рассылает WS-сообщение {type: 'refresh'},
  // а клиент делает window.location.reload().
  const refreshTv = useCallback(async (id) => {
    try {
      await axios.post(`${API}/tournaments/${id}/refresh`, {}, { headers: authHeaders() });
      return true;
    } catch (e) { return false; }
  }, []);

  const getByCode = (code) => {
    if (!code) return undefined;
    if (publicTournaments[code] !== undefined) return publicTournaments[code];
    return tournaments.find((t) => t.code === String(code));
  };

  // ---------- WebSocket ----------
  const subscribeCode = useCallback((code) => {
    if (!code) return () => {};
    let ws;
    let closed = false;
    let reconnectTimer;
    let pingTimer;
    // Дебаунс на события {type: 'refresh'}: при серии быстрых admin-действий
    // сервер шлёт refresh на каждую мутацию. Схлопываем их в один GET через
    // 400 мс, чтобы TV не спамил бэкенд.
    let refreshDebounceTimer = null;
    const REFRESH_DEBOUNCE_MS = 400;
    const syncFromServer = async (force = false) => {
      if (typeof window === 'undefined' || !window.location?.pathname?.startsWith('/clock/')) return;
      try {
        const r = await axios.get(`${API}/clock/${code}`);
        if (!r.data?.id) return;
        const ver = r.data.version || 0;
        const last = lastAppliedVersionRef.current[r.data.id] || 0;
        // Force-refresh: не откатываем более новое состояние; применяем через
        // ingestServerState (lite-merge + banner/assets preserve).
        if (force) {
          if (ver < last) {
            if (typeof r.data.serverNowMs === 'number') {
              updateClockOffset(r.data.serverNowMs);
            }
            return;
          }
          ingestServerState(r.data);
          return;
        }
        if (ver > last) {
          ingestServerState(r.data);
        } else if (typeof r.data.serverNowMs === 'number') {
          updateClockOffset(r.data.serverNowMs);
        }
      } catch (e) { /* noop */ }
    };
    const doRefresh = () => { syncFromServer(true); };
    const scheduleRefresh = () => {
      if (refreshDebounceTimer) clearTimeout(refreshDebounceTimer);
      refreshDebounceTimer = setTimeout(() => {
        refreshDebounceTimer = null;
        doRefresh();
      }, REFRESH_DEBOUNCE_MS);
    };
    const open = () => {
      try { ws = new WebSocket(wsUrlFor(code)); } catch (e) { return; }
      // Watchdog: если после отправленного ping не пришёл ни pong, ни любой
      // другой message — сокет мёртв (типичная ситуация при idle-таймауте
      // прокси/CDN, когда клиент/сервер не получают событие close). Закрываем
      // сокет вручную — сработает onclose и запустит переподключение.
      let pongTimer = null;
      const scheduleWatchdog = () => {
        if (pongTimer) clearTimeout(pongTimer);
        pongTimer = setTimeout(() => {
          try { ws && ws.close(); } catch (e) { /* noop */ }
        }, 8000);
      };
      const clearWatchdog = () => {
        if (pongTimer) { clearTimeout(pongTimer); pongTimer = null; }
      };
      ws.onopen = () => {
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try {
            if (ws && ws.readyState === 1) {
              ws.send('ping');
              scheduleWatchdog();
            }
          } catch (e) { /* noop */ }
        }, 20000);
        // После (пере)подключения принудительно синхронизируем состояние —
        // могли пропустить admin-действия за время «молчания» сокета.
        if (isReconnectRef) scheduleRefresh();
        isReconnectRef = true;
      };
      ws.onmessage = (evt) => {
        // ЛЮБОЕ входящее сообщение (включая pong) — сигнал жизни сокета.
        clearWatchdog();
        try {
          if (evt.data === 'pong') return;
          const msg = JSON.parse(evt.data);
          if (msg.type === 'refresh') {
            // Админ нажал «Обновить ТВ» либо было admin-действие — 
            // синхронизируем только ЗНАЧЕНИЯ (таймер/уровень/игроков/паузу),
            // фон и звуки НЕ трогаем. Никаких reload — сохраняется fullscreen
            // и WS-соединение. Дебаунс защищает от лавины GET при серии
            // быстрых admin-кликов.
            scheduleRefresh();
            return;
          }
          if (msg.type === 'tournament' && msg.data && msg.data.code === code) {
            ingestServerState(msg.data);
          }
        } catch (e) { /* noop */ }
      };
      ws.onclose = () => {
        clearInterval(pingTimer);
        clearWatchdog();
        if (!closed) reconnectTimer = setTimeout(open, 1500);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* noop */ } };
    };
    // Флаг: первый open() — без forced refresh (fetchByCode уже отработал).
    // На каждом переподключении — принудительный refresh для догона пропусков.
    let isReconnectRef = false;
    // Когда таб/окно возвращается в фокус после длительного бездействия,
    // мобильный/десктопный браузер мог заморозить setInterval, из-за чего
    // ping не отправлялся, прокси закрыл сокет — но onclose нам не пришёл
    // (замороженный поток событий). Принудительно тестируем сокет и
    // синхронизируем HTTP-запросом.
    const onVisible = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      // Всегда обновляем через HTTP — самая надёжная синхронизация.
      scheduleRefresh();
      // Если сокет не OPEN — форсируем переподключение.
      if (!ws || ws.readyState !== 1) {
        try { ws && ws.close(); } catch (e) { /* noop */ }
        if (!closed && (!ws || ws.readyState === 3)) {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(open, 100);
        }
        return;
      }
      // Сокет якобы OPEN — но после долгой заморозки он мог быть тихо убит
      // прокси. Отправляем ping и просим watchdog проверить pong.
      try { ws.send('ping'); } catch (e) { /* noop */ }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', onVisible);
    }
    // HTTP-polling только как fallback: когда WS не OPEN (не поднялся /
    // отвалился / прокси режет). При живом сокете хватает tournament-сообщений.
    const POLL_MS = 2000;
    const pollTimer = setInterval(() => {
      if (closed) return;
      if (ws && ws.readyState === 1) return; // OPEN
      syncFromServer(false);
    }, POLL_MS);
    open();
    return () => {
      closed = true;
      clearInterval(pollTimer);
      clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (refreshDebounceTimer) { clearTimeout(refreshDebounceTimer); refreshDebounceTimer = null; }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onVisible);
      }
      try { ws && ws.close(); } catch (e) { /* noop */ }
    };
  }, [ingestServerState]);

  const setSound = (v) => {
    const val = !!v;
    setSoundEnabledState(val);
    try { localStorage.setItem(SOUND_KEY, val ? '1' : '0'); } catch (e) { /* noop */ }
  };

  const value = {
    user,
    tournaments,
    loading,
    soundEnabled,
    login,
    logout,
    listUsers,
    createUser,
    setUserActive,
    deleteUser,
    createTournament,
    updateTournament,
    dispatchAction,
    updateBanner,
    duplicateTournament,
    deleteTournament,
    getById,
    fetchFullTournament,
    getServerNow: () => Date.now() + clockOffsetMsRef.current,
    getByCode,
    fetchByCode,
    fetchClockAssets,
    refreshTv,
    subscribeCode,
    refreshTournaments,
    setSound
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
